import React, { useCallback, useMemo, useRef, useEffect } from "react";
import { useForm, FormContext } from "react-hook-form";
import { Form } from "../styled";
import FormSect from "_c/form/Sect";
import Label from "_c/form/Label";
import Message from "_c/form/Message";
import Checkbox from "_c/Checkbox";
import PT from "prop-types";
import { Button } from "_c/button";
import Calendar from "_c/Calendar";
import { UncontrolledRadio } from "_c/Radio";
import { mul } from "@/utils/money";
import isempty from "lodash/isEmpty";
import { AbstractControlledField } from "_c/form/Field";
import Fieldset from "_c/form/Fieldset";
import FormMessage from "_c/form/Message";
import ButtonGroup from "_c/form/ButtonGroup";
import TextInput from "_c/form/TextInput";
import { discountSchema } from "@/validation/discount";
import { ProgressEmitter } from "_c/GlobalProgress/emitter";
import ClientSelect from "./Client";
import { useNotify } from "@/hooks/useNotify";
import NumberFormat from "react-number-format";
import { analyticsAction } from "@/utils/analytics";

const BonusForm = ({
  discount,
  clients,
  allowedClientsForService,
  allowedClientsForAll,
  service,
  createDiscount,
  updateDiscount,
  masterId,
  onClose,
  t,
  ns,
  ...props
}) => {
  const { notify, notifyError } = useNotify();
  const firstRender = useRef(true);
  const defaultClient = useMemo(() => {
    if (discount?.client && !isempty(clients)) {
      return clients.find(({ value }) => value === discount.client);
    } else {
      return null;
    }
  }, [discount, clients]);
  const defaultValues = useMemo(() => {
    return {
      amount: discount ? mul(discount.amount, 100) : 1,
      model_search: discount ? discount.model_search : false,
      with_expires: discount ? Boolean(discount.expires) : false,
      destroy_after_use: discount ? Boolean(discount.destroy_after_use) : false,
      expires: discount ? [discount.expires] : null,
      for_client: discount ? Boolean(discount.client) : false,
      client: defaultClient,
      discount_type: discount
        ? Boolean(discount.service)
          ? "service"
          : "all"
        : undefined,
    };
  }, [defaultClient, discount]);

  const methods = useForm({
    validationSchema: discountSchema,
    defaultValues,
  });
  const {
    handleSubmit,
    register,
    watch,
    clearError,
    errors,
    setValue,
    setError,
    getValues,
  } = methods;
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
    } else {
      const { client, ...rest } = defaultValues;
      const keys = Object.keys(rest);
      const updatedForm = keys.reduce((acc, field) => {
        const val = rest[field];
        acc.push({ [field]: val });
        return acc;
      }, []);
      setValue(updatedForm);

      // wait a little bit before react-hook-form register this field
      // if there is a client
      setTimeout(() => {
        setValue("client", defaultValues.client);
      }, 150);
    }
  }, [defaultValues, setValue]);
  const watchWithExpires = watch("with_expires");
  const watchForClient = watch("for_client");
  const watchType = watch("discount_type");
  const watchClient = watch("client");
  useEffect(() => {
    if (watchType === "all" || watchForClient) {
      const model = getValues("model_search");
      if (model) {
        setValue("model_search", false);
      }
    }
  }, [watchType, watchForClient, getValues, setValue]);
  const forAllDisabled = useMemo(() => {
    if (discount) {
      return Boolean(discount.service);
    }
    if (props.canCreateMorePublicDiscountsForAll) {
      return false;
    } else {
      return !Boolean(watchForClient);
    }
  }, [props.canCreateMorePublicDiscountsForAll, watchForClient, discount]);
  const forServiceDisabled = useMemo(() => {
    if (discount) {
      return !Boolean(discount.service);
    }
    if (props.canCreateMorePublicDiscountsForService) {
      return false;
    } else {
      return !Boolean(watchForClient);
    }
  }, [props.canCreateMorePublicDiscountsForService, watchForClient, discount]);
  useEffect(() => {
    if (forServiceDisabled) {
      const type = getValues("discount_type");
      if (type === "service") {
        setValue("discount_type", null);
      }
    }
  }, [forServiceDisabled, getValues, setValue]);
  useEffect(() => {
    if (watchClient) {
      if (watchType === "all") {
        if (
          !allowedClientsForAll.some(({ value }) => value === watchClient.value)
        ) {
          setValue("client", null);
        }
      } else if (watchType === "service") {
        if (
          !allowedClientsForService.some(
            ({ value }) => value === watchClient.value
          )
        ) {
          setValue("client", null);
        }
      }
    }
  }, [
    watchClient,
    allowedClientsForAll,
    allowedClientsForService,
    watchType,
    setValue,
  ]);
  const modelDisabled = watchForClient || watchType === "all";
  const onSubmit = useCallback(
    async (form) => {
      clearError("server");
      const norm = {
        master: masterId,
        model_search: form.model_search,
        expires:
          form.with_expires && form.expires
            ? new Date(form.expires[0]).toISOString()
            : undefined,
        amount: form.amount / 100,
        client: form.for_client ? form.client.value : undefined,
        service: form.discount_type === "service" ? service : undefined,
      };
      try {
        ProgressEmitter.start();
        if (discount) {
          await updateDiscount(discount._id, norm);
          notify(t(`${ns}.update_success`));
        } else {
          await createDiscount(norm);
          notify(t(`${ns}.create_success`));
          analyticsAction({
            action: "discount_add_new",
            category: "profile",
          });
        }
        onClose();
      } catch (e) {
        console.error(e);
        notifyError(t([`errors:${e}`, "errors:unspecified_server"]));
        setError("server", "serverError", e);
      } finally {
        ProgressEmitter.end();
      }
    },
    [discount, service, masterId]
  );
  return (
    <FormContext {...methods}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <AbstractControlledField
          Component={NumberFormat}
          customInput={TextInput}
          allowNegative={false}
          isNumericString={true}
          decimalScale={0}
          onChangeName="onValueChange"
          onChange={([{ floatValue }]) => {
            return floatValue;
          }}
          suffix={" %"}
          label={t(`${ns}.amount`)}
          id={`${props.formId}-amount`}
          name="amount"
          t={t}
        />
        <FormSect>
          <Fieldset>
            <Label as="legend">{t(`${ns}.labels.type`)}</Label>
            <UncontrolledRadio
              ref={register}
              id={`${props.formId}-discount_service`}
              name="discount_type"
              value="service"
              label={t(`${ns}.labels.type_this_service`)}
              disabled={forServiceDisabled}
            />
            <UncontrolledRadio
              id={`${props.formId}-discount_all`}
              ref={register}
              name="discount_type"
              value="all"
              label={t(`${ns}.labels.type_all_services`)}
              disabled={forAllDisabled}
            />
            {errors.discount_type && (
              <FormMessage
                error
                message={t([
                  `errors:${errors.discount_type.message}`,
                  "errors:unspecified",
                ])}
              />
            )}
          </Fieldset>
        </FormSect>
        <FormSect>
          <Checkbox
            disabled={modelDisabled}
            ref={register}
            name="model_search"
            label={t(`${ns}.types.model`)}
          />
        </FormSect>
        <FormSect>
          <Checkbox
            id={`${props.formId}-with_expires`}
            ref={register}
            name="with_expires"
            label={t(`${ns}.labels.with_expires`)}
          />
        </FormSect>
        {watchWithExpires && (
          <AbstractControlledField
            Component={Calendar}
            t={t}
            name="expires"
            label={t(`${ns}.labels.expires`)}
            id={`${props.formId}-expires`}
          />
        )}
        <FormSect>
          <Checkbox
            id={`${props.formId}-for_client`}
            ref={register}
            name="for_client"
            label={t(`${ns}.labels.for_client`)}
            disabled={Boolean(discount) || props.disableClientPicker}
          />
          {errors.for_client && (
            <Message error message={errors.for_client.message} />
          )}
        </FormSect>
        {watchForClient && (
          <ClientSelect
            ns={ns}
            formId={props.formId}
            t={t}
            options={
              watchType === "service"
                ? allowedClientsForService
                : allowedClientsForAll
            }
          />
        )}
        {errors.server && (
          <FormMessage
            error
            message={t([
              `errors:${errors.server.message}`,
              "errors:unspecified_server",
            ])}
          />
        )}
        <ButtonGroup>
          <Button style={{ marginRight: "10px" }} greenish type="submit" small>
            {discount ? t("common:change") : t("common:add")}
          </Button>
          <Button type="button" negative onClick={onClose} small>
            {t("common:close")}
          </Button>
        </ButtonGroup>
      </Form>
    </FormContext>
  );
};
BonusForm.propTypes = {
  discount: PT.object,
  clients: PT.array,
  service: PT.string,
  masterId: PT.string.isRequired,
  formId: PT.string,
  canCreateMorePublicDiscountsForAll: PT.bool,
  canCreateMorePublicDiscountsForService: PT.bool,
  allowedClientsForService: PT.array,
  allowedClientsForAll: PT.array,
  disableClientPicker: PT.bool,

  t: PT.func.isRequired,
  onClose: PT.func.isRequired,
  createDiscount: PT.func.isRequired,
  updateDiscount: PT.func.isRequired,
};

export default BonusForm;
